import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';

interface OsrEvent {
  id: string;
  assetId: string;
  assetName: string;
  installedCapacityAcMw: number | null;
  eventKind: string;
  source: string | null;
  startedAtLocal: string;
  endedAtLocal: string | null;
  curtailmentPct: number | null;
  curtailmentPmaxKw: number | null;
  reason: string | null;
  dispatcherName: string | null;
  dispatcherOrg: string | null;
  notes: string | null;
}

interface AssetOption { id: string; assetName: string; installedCapacityMw: number | null; }

const fmtDateTime = (ts: string | null): string => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('uk-UA', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const fmtNum = (v: any, d = 0): string => (v == null || isNaN(Number(v))) ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function OsrPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [events, setEvents] = useState<OsrEvent[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    assetId: '', startedAtLocal: '', curtailmentPct: '', curtailmentPmaxKw: '',
    reason: '', dispatcherName: '', dispatcherOrg: 'ОСР (Облэнерго)', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2';

  const load = () => {
    setLoading(true); setError(null);
    Promise.all([
      api.get<{ events: OsrEvent[] }>('/sko/curtailment-events?days=180&eventKind=osr_command'),
      api.get<{ assets: AssetOption[] }>('/sko/assets'),
    ]).then(([ev, as]) => {
      setEvents(ev.events || []);
      setAssets(as.assets || []);
    }).catch((e) => setError(e instanceof ApiError ? getApiErrorMessage(e, 'Failed to load') : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upd = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.assetId || !form.startedAtLocal) {
      setError('Оберіть актив та час початку.'); return;
    }
    setSaving(true); setError(null);
    try {
      await api.post('/sko/curtailment-events', {
        assetId: form.assetId,
        eventKind: 'osr_command',
        source: 'dispatcher_phone',
        startedAtLocal: form.startedAtLocal,
        curtailmentPct: form.curtailmentPct ? Number(form.curtailmentPct) : null,
        curtailmentPmaxKw: form.curtailmentPmaxKw ? Number(form.curtailmentPmaxKw) : null,
        reason: form.reason || null,
        dispatcherName: form.dispatcherName || null,
        dispatcherOrg: form.dispatcherOrg || null,
        notes: form.notes || null,
      });
      setShowForm(false);
      setForm({ assetId: '', startedAtLocal: '', curtailmentPct: '', curtailmentPmaxKw: '',
                reason: '', dispatcherName: '', dispatcherOrg: 'ОСР (Облэнерго)', notes: '' });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? getApiErrorMessage(e, 'Failed to save') : String(e));
    } finally { setSaving(false); }
  };

  const closeEvent = async (id: string) => {
    if (!confirm('Закрити цю подію ОСР?')) return;
    try {
      await api.patch(`/sko/curtailment-events/${id}`, { endedAtLocal: new Date().toISOString().slice(0, 19) });
      load();
    } catch (e) {
      alert(e instanceof ApiError ? getApiErrorMessage(e, 'Failed') : String(e));
    }
  };

  const active = events.filter(e => !e.endedAtLocal);
  const past = events.filter(e => !!e.endedAtLocal);

  return (
    <DashboardLayout>
      <div className="px-6 py-6 w-full">
        <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: textMain }}>{t('osr_page_title')}</h1>
            <p className="text-sm mt-1" style={{ color: textMuted }}>{t('osr_page_subtitle')}</p>
          </div>
          <button
            type="button" onClick={() => setShowForm(s => !s)}
            className="text-sm px-4 py-2 rounded-md cursor-pointer font-medium"
            style={{ background: '#D97706', color: '#fff', border: 'none' }}>
            <i className="ri-add-line mr-1"></i> {showForm ? t('osr_cancel') : t('osr_log_new')}
          </button>
        </div>

        <div className="mb-4 p-4 rounded-lg text-sm" style={{ background: isDark ? 'rgba(217,119,6,0.08)' : '#FEF3C7', border: `1px solid ${isDark ? 'rgba(217,119,6,0.25)' : '#FDE68A'}`, color: isDark ? '#FCD34D' : '#92400E' }}>
          <i className="ri-information-line mr-1"></i>
          <strong>{t('osr_info_title')}</strong> — {t('osr_info_body')}
        </div>

        {showForm && (
          <div className="mb-6 p-5 rounded-lg" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <h3 className="text-base font-semibold mb-3" style={{ color: textMain }}>{t('osr_log_form_title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <Field label={t('osr_field_asset')} required>
                <select value={form.assetId} onChange={e => upd('assetId', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }}>
                  <option value="">— {t('osr_select_asset')} —</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>{a.assetName} ({a.installedCapacityMw} МВт)</option>
                  ))}
                </select>
              </Field>
              <Field label={t('osr_field_started')} required>
                <input type="datetime-local" value={form.startedAtLocal} onChange={e => upd('startedAtLocal', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
              </Field>
              <Field label={t('osr_field_pct')}>
                <input type="number" min="0" max="100" step="1" placeholder="30" value={form.curtailmentPct} onChange={e => upd('curtailmentPct', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
              </Field>
              <Field label={t('osr_field_pmax_kw')}>
                <input type="number" min="0" step="1" placeholder="1500" value={form.curtailmentPmaxKw} onChange={e => upd('curtailmentPmaxKw', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
              </Field>
              <Field label={t('osr_field_dispatcher')}>
                <input type="text" placeholder="Ім'я диспетчера" value={form.dispatcherName} onChange={e => upd('dispatcherName', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
              </Field>
              <Field label={t('osr_field_org')}>
                <input type="text" value={form.dispatcherOrg} onChange={e => upd('dispatcherOrg', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
              </Field>
            </div>
            <Field label={t('osr_field_reason')}>
              <textarea rows={2} value={form.reason} onChange={e => upd('reason', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} placeholder="Напр.: ремонт трансформатору 35/10" />
            </Field>
            <Field label={t('osr_field_notes')}>
              <textarea rows={2} value={form.notes} onChange={e => upd('notes', e.target.value)} className="w-full px-3 py-2 rounded text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMain }} />
            </Field>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded text-sm cursor-pointer" style={{ background: 'transparent', border: `1px solid ${cardBorder}`, color: textMuted }}>{t('osr_cancel')}</button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 rounded text-sm font-medium cursor-pointer" style={{ background: '#D97706', color: '#fff', border: 'none', opacity: saving ? 0.6 : 1 }}>
                {saving ? t('osr_saving') : t('osr_save')}
              </button>
            </div>
          </div>
        )}

        {error && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'var(--surface-2)', color: 'var(--err)', border: '1px solid color-mix(in srgb, var(--err) 35%, transparent)' }}>{error}</div>}

        <Section title={`${t('osr_section_active')} (${active.length})`} cardBg={cardBg} cardBorder={cardBorder} textMain={textMain}>
          {loading ? <Empty text={t('common_loading') || 'Завантаження…'} muted={textMuted}/> :
           active.length === 0 ? <Empty text={t('osr_empty_active')} muted={textMuted}/> :
            <EventTable events={active} onClose={closeEvent} textMain={textMain} textMuted={textMuted} tableHeaderBg={tableHeaderBg} tableBorder={tableBorder} t={t} />}
        </Section>

        <div className="mt-4">
          <Section title={`${t('osr_section_past')} (${past.length})`} cardBg={cardBg} cardBorder={cardBorder} textMain={textMain}>
            {past.length === 0 ? <Empty text={t('osr_empty_past')} muted={textMuted}/> :
              <EventTable events={past} textMain={textMain} textMuted={textMuted} tableHeaderBg={tableHeaderBg} tableBorder={tableBorder} t={t} />}
          </Section>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: any }) {
  return (
    <div className="mb-2">
      <label className="block text-xs font-medium mb-1">{label}{required && <span className="text-[var(--text-dim)] ml-1">*</span>}</label>
      {children}
    </div>
  );
}

function Section({ title, children, cardBg, cardBorder, textMain }: any) {
  return (
    <div className="rounded-lg" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="px-4 py-3 font-semibold text-sm" style={{ color: textMain, borderBottom: `1px solid ${cardBorder}` }}>{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ text, muted }: { text: string; muted: string }) {
  return <div className="py-8 text-center text-sm" style={{ color: muted }}>{text}</div>;
}

function EventTable({ events, onClose, textMain, textMuted, tableHeaderBg, tableBorder, t }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead style={{ background: tableHeaderBg }}>
          <tr>
            <th className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_asset')}</th>
            <th className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_started')}</th>
            <th className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_ended')}</th>
            <th className="text-right px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_limit')}</th>
            <th className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_dispatcher')}</th>
            <th className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: textMuted }}>{t('osr_col_reason')}</th>
            {onClose && <th className="px-3 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {events.map((e: OsrEvent) => (
            <tr key={e.id} style={{ borderBottom: `1px solid ${tableBorder}` }}>
              <td className="px-3 py-2 font-medium" style={{ color: textMain }}>{e.assetName}</td>
              <td className="px-3 py-2" style={{ color: textMuted }}>{fmtDateTime(e.startedAtLocal)}</td>
              <td className="px-3 py-2" style={{ color: textMuted }}>{e.endedAtLocal ? fmtDateTime(e.endedAtLocal) : <span style={{ color: '#D97706', fontWeight: 600 }}>{t('osr_status_active')}</span>}</td>
              <td className="px-3 py-2 text-right" style={{ color: textMain }}>
                {e.curtailmentPmaxKw != null ? `${fmtNum(e.curtailmentPmaxKw)} кВт` : '—'}
                {e.curtailmentPct != null && <div className="text-xs" style={{ color: textMuted }}>({e.curtailmentPct}%)</div>}
              </td>
              <td className="px-3 py-2 text-xs" style={{ color: textMuted }}>{e.dispatcherName || '—'}<br/>{e.dispatcherOrg || ''}</td>
              <td className="px-3 py-2 text-xs max-w-md truncate" title={e.reason || ''} style={{ color: textMuted }}>{e.reason || '—'}</td>
              {onClose && (
                <td className="px-3 py-2">
                  <button onClick={() => onClose(e.id)} className="text-xs px-2 py-1 rounded cursor-pointer" style={{ background: 'transparent', border: '1px solid #D97706', color: '#D97706' }}>{t('osr_close_event')}</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
